import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BarChart3 } from 'lucide-react';
import { VizSwitcher, type VizType } from '../blocks/VizSwitcher';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { PanelHost } from './PanelHost';
import { useViewTheme } from './viewTheme';
import type { DatasetMap, LayoutItem, ViewPanel, ViewSpec } from './types';

/** Høyde per rad i rutenettet (layout.h = antall rader). */
export const ROW_HEIGHT = 80;
const GAP = 12;
const PANEL_HEADER = 36;

interface ViewRendererProps {
    view: ViewSpec;
    datasets: DatasetMap;
    /** Kompakt visning (dashboard-widget): skjuler tittel/beskrivelse */
    compact?: boolean;
    /** Vis bare dette panelet (widget med ett panel) */
    onlyPanelId?: string;
    /** Brukeren byttet graftype i et panel (VizSwitcher) — oppdaterer visningen */
    onViewChange?: (view: ViewSpec) => void;
}

type Mode = 'desktop' | 'tablet' | 'phone';

function useContainerMode(ref: React.RefObject<HTMLDivElement>): Mode {
    const [mode, setMode] = useState<Mode>('desktop');
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const update = (width: number) => setMode(width < 640 ? 'phone' : width < 900 ? 'tablet' : 'desktop');
        update(el.getBoundingClientRect().width);
        const observer = new ResizeObserver(entries => update(entries[0].contentRect.width));
        observer.observe(el);
        return () => observer.disconnect();
    }, [ref]);
    return mode;
}

/**
 * Viser en Compass-visning (View Spec v1) som rutenett: 12 kolonner på bred skjerm, 6 kolonner på nettbrett
 * (brede paneler fyller raden, smale tar halvparten), og paneler under hverandre på telefon.
 * All data kommer fra datasettene — ingen tokens og ingen nettverkskall.
 */
export const ViewRenderer: React.FC<ViewRendererProps> = ({ view, datasets, compact, onlyPanelId, onViewChange }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const mode = useContainerMode(containerRef);
    const theme = useViewTheme();

    const items = useMemo(() => {
        const byId = new Map(view.panels.map(p => [p.id, p]));
        const layout: LayoutItem[] = view.layout.items.filter(i => byId.has(i.panel));
        const sorted = [...layout].sort((a, b) => a.y - b.y || a.x - b.x);
        return (onlyPanelId ? sorted.filter(i => i.panel === onlyPanelId) : sorted)
            .map(i => ({ item: i, panel: byId.get(i.panel)! }));
    }, [view, onlyPanelId]);

    const handleChartTypeChange = (panelId: string, type: VizType) => {
        if (!onViewChange) return;
        onViewChange({
            ...view,
            panels: view.panels.map(p => p.id !== panelId ? p : type === 'table'
                ? { ...p, kind: 'table', config: { columns: [p.config?.xAxis?.dataKey, ...(p.config?.series ?? []).map((s: any) => s.dataKey)].filter(Boolean) } }
                : { ...p, config: { ...p.config, chartType: type } })
        });
    };

    const gridStyle: React.CSSProperties = mode === 'desktop'
        ? { display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', gridAutoRows: `${ROW_HEIGHT}px`, gap: GAP }
        : mode === 'tablet'
            ? { display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gridAutoRows: `${ROW_HEIGHT}px`, gap: GAP, gridAutoFlow: 'row dense' }
            : { display: 'flex', flexDirection: 'column', gap: GAP };

    const placement = (i: LayoutItem): React.CSSProperties => {
        if (onlyPanelId) return { gridColumn: '1 / -1', gridRow: `span ${i.h}` };
        if (mode === 'desktop') return { gridColumn: `${i.x + 1} / span ${i.w}`, gridRow: `${i.y + 1} / span ${i.h}` };
        if (mode === 'tablet') return { gridColumn: `span ${i.w >= 7 ? 6 : 3}`, gridRow: `span ${i.h}` };
        return { height: i.h * ROW_HEIGHT };
    };

    return (
        <div ref={containerRef} className="compass-view w-full">
            {!compact && (view.title || view.description) && (
                <div className="mb-3">
                    {view.title && <h3 className="text-base font-semibold text-gray-800">{view.title}</h3>}
                    {view.description && <p className="text-xs text-gray-500">{view.description}</p>}
                </div>
            )}
            <div style={gridStyle}>
                {items.map(({ item, panel }) => (
                    <PanelFrame
                        key={panel.id}
                        panel={panel}
                        style={placement(item)}
                        onChartTypeChange={onViewChange && panel.kind === 'chart' ? (type) => handleChartTypeChange(panel.id, type) : undefined}
                        contentHeight={item.h * ROW_HEIGHT + (item.h - 1) * GAP - PANEL_HEADER - 16}
                    >
                        {(height) => (
                            <PanelHost
                                panel={panel}
                                datasets={datasets}
                                theme={theme}
                                height={height}
                            />
                        )}
                    </PanelFrame>
                ))}
            </div>
        </div>
    );
};

const PanelFrame: React.FC<{
    panel: ViewPanel;
    style: React.CSSProperties;
    contentHeight: number;
    /** Graftypen kan byttes (chart-paneler) — vises bak én knapp i stedet for en ikonrad over grafen */
    onChartTypeChange?: (type: VizType) => void;
    children: (height: number) => React.ReactNode;
}> = ({ panel, style, contentHeight, onChartTypeChange, children }) => {
    const { t } = useTranslation();
    const [switcherOpen, setSwitcherOpen] = useState(false);
    const showHeader = ((panel.title || panel.subtitle) && panel.kind !== 'text') || onChartTypeChange;
    return (
        <section className="compass-view-panel rounded-lg p-2 flex flex-col min-w-0 overflow-hidden relative" style={style} data-panel-id={panel.id}>
            {showHeader && (
                <header className="px-1 mb-1 flex-shrink-0 flex items-start gap-1" style={{ minHeight: PANEL_HEADER - 8 }}>
                    <div className="flex-1 min-w-0">
                        {panel.title && <h4 className="text-sm font-semibold text-gray-700 truncate">{panel.title}</h4>}
                        {panel.subtitle && <p className="text-xs text-gray-500 truncate">{panel.subtitle}</p>}
                    </div>
                    {onChartTypeChange && (
                        <button
                            onClick={() => setSwitcherOpen(o => !o)}
                            aria-expanded={switcherOpen}
                            aria-label={t('manager.modules.claudeQuery.view.changeChart', {}, 'Endre graf')}
                            // Stor trykkflate uten å gjøre panelhodet høyere
                            className={`flex items-center justify-center min-w-[44px] min-h-[44px] -my-2 -mr-1 rounded-lg flex-shrink-0 transition-colors ${switcherOpen
                                ? 'bg-blue-50 text-blue-600'
                                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
                        >
                            <BarChart3 size={16} />
                        </button>
                    )}
                </header>
            )}
            {switcherOpen && onChartTypeChange && (
                <div className="absolute right-2 top-10 z-10 bg-white border border-gray-200 rounded-lg shadow-lg p-1">
                    <VizSwitcher
                        currentType={(panel.config?.chartType ?? 'bar') as VizType}
                        onSwitch={(type) => { setSwitcherOpen(false); onChartTypeChange(type); }}
                        sourceBlockType="chart"
                        iconOnly
                    />
                </div>
            )}
            <div className="flex-1 min-h-0">{children(Math.max(120, contentHeight))}</div>
        </section>
    );
};
